import { useRef, useState, useEffect } from 'react'

interface Selection {
  x: number
  y: number
  w: number
  h: number
}

interface ManualCropperProps {
  file: File
  onFinished: (blobs: Blob[]) => void
  onCancel: () => void
}

export function ManualCropper({ file, onFinished, onCancel }: ManualCropperProps) {
  const imgRef = useRef<HTMLImageElement>(null)
  const [imgUrl, setImgUrl] = useState('')
  const [imgDim, setImgDim] = useState({ w: 0, h: 0 })
  const [selections, setSelections] = useState<Selection[]>([])
  const [current, setCurrent] = useState<Selection | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startPos, setStartPos] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const url = URL.createObjectURL(file)
    setImgUrl(url)
    const img = new Image()
    img.onload = () => setImgDim({ w: img.width, h: img.height })
    img.src = url
    return () => URL.revokeObjectURL(url)
  }, [file])

  const getPointInImage = (e: React.MouseEvent) => {
    if (!imgRef.current) return null
    const imgRect = imgRef.current.getBoundingClientRect()
    const x = e.clientX - imgRect.left
    const y = e.clientY - imgRect.top
    return {
      x: Math.max(0, Math.min(x, imgRect.width)),
      y: Math.max(0, Math.min(y, imgRect.height)),
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    const point = getPointInImage(e)
    if (!point) return
    const { x, y } = point
    setStartPos({ x, y })
    setCurrent({ x, y, w: 0, h: 0 })
    setIsDragging(true)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return
    const point = getPointInImage(e)
    if (!point) return
    const { x, y } = point

    const newX = Math.min(x, startPos.x)
    const newY = Math.min(y, startPos.y)
    const newW = Math.abs(x - startPos.x)
    const newH = Math.abs(y - startPos.y)

    setCurrent({ x: newX, y: newY, w: newW, h: newH })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
    if (current && (current.w < 10 || current.h < 10)) {
      setCurrent(null)
    }
  }

  const addSelection = () => {
    if (current) {
      setSelections([...selections, current])
      setCurrent(null)
    }
  }

  const removeSelection = (idx: number) => {
    setSelections(selections.filter((_, i) => i !== idx))
  }

  const finish = async () => {
    if (selections.length === 0 && current) {
      // If user has a current selection but hasn't "Added" it, assume they want it
      const finalSels = [...selections, current]
      await processAndFinish(finalSels)
    } else {
      await processAndFinish(selections)
    }
  }

  const processAndFinish = async (sels: Selection[]) => {
    if (sels.length === 0) return onCancel()
    if (!imgRef.current) return

    const img = new Image()
    img.src = imgUrl
    await new Promise((res) => (img.onload = res))

    const scaleX = img.width / imgRef.current.width
    const scaleY = img.height / imgRef.current.height

    const blobs: Blob[] = []
    for (const s of sels) {
      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.round(s.w * scaleX))
      canvas.height = Math.max(1, Math.round(s.h * scaleY))
      const ctx = canvas.getContext('2d')
      if (ctx) {
        const srcX = Math.round(s.x * scaleX)
        const srcY = Math.round(s.y * scaleY)
        const srcW = Math.round(s.w * scaleX)
        const srcH = Math.round(s.h * scaleY)
        ctx.drawImage(
          img,
          srcX,
          srcY,
          srcW,
          srcH,
          0,
          0,
          canvas.width,
          canvas.height
        )
        const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.95))
        if (blob) blobs.push(blob)
      }
    }
    onFinished(blobs)
  }

  return (
    <div className="cropper-overlay" onClick={onCancel}>
      <div className="cropper-content" onClick={(e) => e.stopPropagation()}>
        <div className="cropper-header">
          <div className="cropper-title">手動裁切上傳</div>
          <div className="row" style={{ gap: '0.5rem' }}>
            <button className="btn btn-sm" onClick={onCancel}>取消</button>
            <button 
              className="btn primary btn-sm" 
              onClick={finish}
              disabled={selections.length === 0 && !current}
            >
              完成並上載 ({selections.length + (current ? 1 : 0)} 段)
            </button>
          </div>
        </div>

        <div className="cropper-body">
          <div className="cropper-main">
            <div className="cropper-hint">
              提示：在圖片上點擊並拖動以框選範圍。您可以多次框選不同段落。
            </div>
            <div 
              className="cropper-canvas-container"
            >
              <div
                className="cropper-stage"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                style={{ cursor: 'crosshair' }}
              >
                {imgUrl && <img ref={imgRef} src={imgUrl} draggable={false} className="cropper-img" />}
                
                {/* Previous selections */}
                {selections.map((s, i) => (
                  <div
                    key={i}
                    className="selection-box saved"
                    style={{ left: s.x, top: s.y, width: s.w, height: s.h }}
                  >
                    <span className="selection-label">#{i + 1}</span>
                    <button className="selection-remove" onClick={(e) => { e.stopPropagation(); removeSelection(i) }}>×</button>
                  </div>
                ))}

                {/* Current selection */}
                {current && (
                  <div
                    className="selection-box active"
                    style={{ left: current.x, top: current.y, width: current.w, height: current.h }}
                  >
                    {!isDragging && (
                      <button className="selection-add" onClick={(e) => { e.stopPropagation(); addSelection() }}>新增此範圍</button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="cropper-sidebar">
            <div className="sidebar-title">已選取區段</div>
            <div className="sidebar-list">
              {selections.length === 0 && !current && <div className="muted">尚未框選...</div>}
              {selections.map((s, i) => (
                <div key={i} className="sidebar-item">
                  <span>區段 #{i + 1}</span>
                  <button className="btn btn-sm" onClick={() => removeSelection(i)}>移除</button>
                </div>
              ))}
              {current && !isDragging && (
                <div className="sidebar-item pending">
                  <span>新選取範圍...</span>
                  <button className="btn primary btn-sm" onClick={addSelection}>確定新增</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
